import React, { useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AppState } from 'redux/config/store'
import { Locations } from 'shared/Router/Locations'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import Button from 'core-system/Button'
import Modal from 'core-system/Modal'
import palette from 'core-system/Themes/palette'
import EnrolledProgramRow, {
  ProgramTagType,
  ServiceItem,
} from './EnrolledProgramRow'
import AvailableProgramCard, {
  AvailableProgramData,
} from './AvailableProgramCard'
import FormattingUtils from 'shared/FormattingUtils'
import ridepandaLogo from '../../assets/ridepanda-logo.png'
import baywheelsLogo from '../../assets/baywheels-logo.png'
import RidePandaModal from './RidePandaModal'
import ClipperEnrollModal from './ClipperEnrollModal'

const SectionContainer = styled.div`
  ${(props) => props.theme.baseCard};
  display: flex;
  flex-direction: column;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`

const CountText = styled(Text)`
  color: ${(props) => props.theme.palette.text.placeholder};
`

const SubsectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  color: ${(props) => props.theme.palette.text.placeholder};
  margin-bottom: 0.75rem;
  margin-top: 1.5rem;

  &:first-of-type {
    margin-top: 0;
  }
`

const EnrolledHeader = styled(SubsectionHeader)`
  color: ${(props) => props.theme.palette.success.green600};
  font-weight: 600;
`

const CheckIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M20 6L9 17L4 12'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const EnrolledList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const AvailableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: ${(props) => props.theme.breakpoints[2]}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    grid-template-columns: 1fr;
  }
`

const ModalContent = styled.div`
  padding: 1.5rem;
  text-align: center;
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid ${palette.grey.grey300};
  border-radius: 0.5rem;
  font-family: inherit;
  font-size: 0.875rem;
  resize: vertical;
  margin: 1rem 0 1.5rem 0;

  &:focus {
    outline: none;
    border-color: ${palette.primary.pink500};
  }
`

const SuccessIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background-color: ${palette.success.green100};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem auto;
`

interface EnrolledProgram {
  id: string
  name: string
  description: string
  tagType: ProgramTagType
  iconType: 'pretax' | 'micromobility' | 'grh' | 'flex'
  path: string
  services?: ServiceItem[]
}

const MyProgramsSection: React.FC = () => {
  const { programs, preTaxPrograms, profileData } = useSelector(
    (state: AppState) => state.employeePlatform
  )
  const navigate = useNavigate()

  // Enrollment modal state
  const [enrollModalOpen, setEnrollModalOpen] = useState(false)
  const [selectedProgram, setSelectedProgram] = useState<string | null>(null)
  const [additionalNotes, setAdditionalNotes] = useState('')
  const [requestSent, setRequestSent] = useState(false)
  const [pendingEnrollments, setPendingEnrollments] = useState<Set<string>>(
    new Set()
  )
  const [ridePandaModalOpen, setRidePandaModalOpen] = useState(false)
  const [clipperModalOpen, setClipperModalOpen] = useState(false)

  const handleEnrollClick = (programName: string) => {
    if (programName === 'RidePanda Micromobility') {
      setRidePandaModalOpen(true)
      return
    }
    if (programName === 'Clipper Bay Pass') {
      setClipperModalOpen(true)
      return
    }
    setSelectedProgram(programName)
    setEnrollModalOpen(true)
    setRequestSent(false)
    setAdditionalNotes('')
  }

  const handleConfirmEnroll = () => {
    if (selectedProgram) {
      setPendingEnrollments((prev) => new Set(prev).add(selectedProgram))
    }
    setRequestSent(true)
  }

  const handleCloseModal = () => {
    setEnrollModalOpen(false)
    setSelectedProgram(null)
    setAdditionalNotes('')
    setRequestSent(false)
  }

  // Build enrolled programs list from Redux data
  const enrolledPrograms: EnrolledProgram[] = []

  // Add Pre-Tax programs if available
  const hasTaxSavings =
    preTaxPrograms &&
    profileData?.finchEmploymentType !== 'contractor' &&
    Object.values(preTaxPrograms).some((program) => program !== null)

  if (hasTaxSavings && preTaxPrograms?.COMMUTER_TRANSIT) {
    enrolledPrograms.push({
      id: 'pretax-transit',
      name: 'Commuter Pre-Tax',
      description: `Up to ${FormattingUtils.formatDollar(34000)}/mo tax-free`,
      tagType: 'PRE-TAX',
      iconType: 'pretax',
      path: Locations.EmployeePreTax.Overview,
      services: [
        { type: 'logo', label: 'Clipper Bay Pass', logoUrl: 'https://www.clipperbaypass.com/wp-content/uploads/2023/02/Clipper-BayPass_Logo_color.svg' },
        { type: 'text', label: 'Rail' },
        { type: 'text', label: 'Bus' },
        { type: 'text', label: 'Parking' },
      ],
    })
  }

  // Add other programs from allPrograms
  if (programs?.allPrograms) {
    Object.entries(programs.allPrograms).forEach(([type, typePrograms]) => {
      Object.values(typePrograms).forEach((program) => {
        if (program.status === 'ACTIVE') {
          let tagType: ProgramTagType = 'EMPLOYER-PAID'
          let iconType: 'pretax' | 'micromobility' | 'grh' | 'flex' =
            'micromobility'
          let path = Locations.EmployeeDashboard

          if (type === 'MICROMOBILITY') {
            iconType = 'micromobility'
            tagType = 'EMPLOYER-PAID'
            enrolledPrograms.push({
              id: program.id,
              name: program.name || 'Ridepanda Micromobility',
              description: `${FormattingUtils.formatDollar(7000)}/month budget`,
              tagType,
              iconType,
              path,
              services: [
                { type: 'logo', label: 'Ridepanda', logoUrl: ridepandaLogo },
                { type: 'logo', label: 'Bay Wheels', logoUrl: baywheelsLogo },
              ],
            })
          } else if (type === 'GRH') {
            iconType = 'grh'
            tagType = 'EMPLOYER-PAID'
            path = Locations.EmployeeGRH.Overview
            enrolledPrograms.push({
              id: program.id,
              name: 'Guaranteed Ride Home',
              description: `${program.remainingUses || 6} trips/year`,
              tagType,
              iconType,
              path,
              services: [
                { type: 'text', label: 'Rideshare' },
                { type: 'text', label: 'Taxi' },
              ],
            })
          } else if (type === 'FLEX') {
            iconType = 'flex'
            tagType = 'EMPLOYER-PAID'
            path = Locations.EmployeeFlex.Overview
            enrolledPrograms.push({
              id: program.id,
              name: program.name || 'Flex Program',
              description: '$60.00/month budget',
              tagType,
              iconType,
              path,
              services: [{ type: 'text', label: 'All Transportation' }],
            })
          }
        }
      })
    })
  }

  // Mock available programs for demo (these would typically come from an API)
  const availablePrograms: AvailableProgramData[] = [
    {
      id: 'baypass',
      type: 'transit',
      name: 'Clipper Bay Pass',
      description:
        'Unlimited rides on BART, Muni, Caltrain, and 20+ Bay Area transit agencies via your Clipper card',
      tagVariant: 'employer',
      tagLabel: 'EMPLOYER-PAID',
      benefitText: 'Unlimited regional transit',
      logoUrl: 'https://www.clipperbaypass.com/wp-content/uploads/2023/02/Clipper-BayPass_Logo_color.svg',
    },
    {
      id: 'ridepanda',
      type: 'micromobility',
      name: 'RidePanda Micromobility',
      description:
        'E-bikes, scooters & pedal bikes — includes theft insurance, free maintenance & welcome kit',
      tagVariant: 'employer',
      tagLabel: 'EMPLOYER-PAID',
      benefitText: '$100/mo stipend · bikes from $65/mo',
    },
    {
      id: 'hqparking',
      type: 'parking',
      name: 'HQ Parking',
      description: 'Reserved parking at SF headquarters',
      tagVariant: 'pretax',
      tagLabel: 'PRE-TAX ELIGIBLE',
      benefitText: 'Guaranteed spot at HQ',
    },
  ]

  const enrolledCount = enrolledPrograms.length
  const availableCount = availablePrograms.length

  return (
    <SectionContainer>
      <SectionHeader>
        <Text variant='h4'>My Programs</Text>
        <CountText variant='body2'>
          {enrolledCount} enrolled · {availableCount} available
        </CountText>
      </SectionHeader>

      {enrolledCount > 0 && (
        <>
          <EnrolledHeader>
            <CheckIcon />
            ENROLLED
          </EnrolledHeader>
          <EnrolledList>
            {enrolledPrograms.map((program) => (
              <EnrolledProgramRow
                key={program.id}
                name={program.name}
                description={program.description}
                tagType={program.tagType}
                iconType={program.iconType}
                services={program.services}
                onManage={() => navigate(program.path)}
              />
            ))}
          </EnrolledList>
        </>
      )}

      {availableCount > 0 && (
        <>
          <SubsectionHeader>AVAILABLE TO ENROLL</SubsectionHeader>
          <AvailableGrid>
            {availablePrograms.map((program) => (
              <AvailableProgramCard
                key={program.id}
                type={program.type}
                name={program.name}
                description={program.description}
                tagVariant={program.tagVariant}
                tagLabel={program.tagLabel}
                benefitText={program.benefitText}
                onClick={() => handleEnrollClick(program.name)}
                isPending={pendingEnrollments.has(program.name)}
                logoUrl={program.logoUrl}
              />
            ))}
          </AvailableGrid>
        </>
      )}

      {/* RidePanda AI recommender modal */}
      <RidePandaModal
        open={ridePandaModalOpen}
        onClose={() => setRidePandaModalOpen(false)}
      />

      {/* Clipper Bay Pass enrollment modal */}
      <ClipperEnrollModal
        open={clipperModalOpen}
        onClose={() => setClipperModalOpen(false)}
      />

      {/* Enrollment Confirmation Modal */}
      <Modal open={enrollModalOpen} onClose={handleCloseModal}>
        <ModalContent>
          {!requestSent ? (
            <>
              <Text variant='h4' marginBottom='1rem'>
                Enroll in Program
              </Text>
              <Text variant='body1' marginBottom='0.5rem'>
                Are you sure you want to enroll in{' '}
                <strong>{selectedProgram}</strong>?
              </Text>
              <Text
                variant='body2'
                textColor='secondary'
                textAlign='left'
                marginTop='1rem'
              >
                Anything else you want to add?
              </Text>
              <TextArea
                placeholder='Add any notes or comments here (optional)'
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
              />
              <FlexContainer
                justifyContent='space-between'
                style={{ width: '100%' }}
              >
                <Button variant='secondary' onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button variant='primary' onClick={handleConfirmEnroll}>
                  Confirm
                </Button>
              </FlexContainer>
            </>
          ) : (
            <>
              <SuccessIcon>
                <svg
                  width='32'
                  height='32'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M20 6L9 17L4 12'
                    stroke={palette.success.green500}
                    strokeWidth='3'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </SuccessIcon>
              <Text variant='h4' marginBottom='1rem'>
                Your request has been sent!
              </Text>
              <Text variant='body2' textColor='secondary' marginBottom='1.5rem'>
                We'll notify you once your enrollment in {selectedProgram} is
                confirmed.
              </Text>
              <Button variant='primary' onClick={handleCloseModal}>
                Done
              </Button>
            </>
          )}
        </ModalContent>
      </Modal>
    </SectionContainer>
  )
}

export default React.memo(MyProgramsSection)
